import { LuFileSpreadsheet } from 'react-icons/lu';
import { pdfjs, Document, Page } from 'react-pdf';
import 'react-pdf/dist/Page/TextLayer.css';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { useDisclosure } from '@chakra-ui/hooks';
import { useNavigate, useParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { Modal, ModalBody, ModalContent, ModalOverlay } from '@chakra-ui/modal';

import {
    SendDeliveryNote,
    addSavHistoryEntry,
    deleteOrder,
    getOrder,
    getOrderNote,
    getOrderDeliveryNote,
    getOrderDeliveryNoteWithoutPrice,
    getPaginatedOrders,
    markOrderReady,
    resolveOrderIssue,
    resolveSavCase,
    updateOrder,
} from 'interfaces/order';
import { getUsersLight } from 'interfaces/user';
import Card from 'components/card';
import { getCustomer, getCustomers, searchCustomersForSelect } from 'interfaces/customer';
import { getProductsByIds } from 'interfaces/products';
import InputField from 'components/fields/InputField';
import SearchSelector from 'components/fields/SearchSelector';
import OrderTable from './components/OrderTable';
import { getTotalHT, getTotalTTC, getGlobalDiscountAmount, isCustomerHT, toNumber } from '../createOrders/utils';
import GoBack from 'components/actions/GoBack';
import DeleteOrder from './components/Delete';
import GenerateInvoice from './components/GenerateInvoice';
import { generateInvoiceForSpecificOrder, getNotPaidInvoicedPerCustomer } from 'interfaces/invoice';
import { getStock } from 'interfaces/stock';
import { FaArrowUpLong } from 'react-icons/fa6';
import { FaExternalLinkAlt } from 'react-icons/fa';
import { productsWithDisplayKey } from '../createOrders/utils/display';
import { getDeliveryTours } from 'interfaces/deliveryTour';
import { MdEmail } from 'react-icons/md';
import GeneratePreparationButton from './components/GeneratePreparationButton';
import AddProductsModal from '../createOrders/components/AddProductModal';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const SAV_STATUS_OPTIONS = [
    { label: 'Ouvert', _id: 'open' },
    { label: 'En cours', _id: 'in_progress' },
    { label: 'Attente client', _id: 'waiting_customer' },
    { label: 'Résolu', _id: 'resolved' },
];

const SAV_PRIORITY_OPTIONS = [
    { label: 'Critique', _id: 'critical' },
    { label: 'Haute', _id: 'high' },
    { label: 'Moyenne', _id: 'medium' },
    { label: 'Basse', _id: 'low' },
];

const EditOrder = () => {
    const navigate = useNavigate();
    const params = useParams();
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { isOpen, onOpen, onClose } = useDisclosure();
    const {
        isOpen: isOpenGenerateInvoice,
        onOpen: onOpenGenerateInvoice,
        onClose: onCloseGenerateInvoice,
    } = useDisclosure();
    const {
        isOpen: isOpenAddProduct,
        onOpen: onOpenAddProduct,
        onClose: onCloseAddProduct,
    } = useDisclosure();
    const [warning, setWarning] = useState<any>(null);
    const [isReady, setIsReady] = useState<boolean>(false);
    const [quantityChanged, setQuantityChanged] = useState<boolean>(false);
    const [customers, setCustomers] = useState<any>([]);
    const [products, setProducts] = useState<any>([]);
    const [deliveryTours, setDeliveryTours] = useState<any>([]);
    const [defaultDeliveryTour, setDefaultDeliveryTour] = useState<any>(true);
    const [isFullyLoaded, setIsFullyLoaded] = useState<boolean>(false);
    const [order, setOrder] = useState<any>(null);
    const [defaultCustomer, setDefaultCustomer] = useState<any>(null);
    const [newItem, setNewItem] = useState<any>({
        discountType: 'none',
        discountValue: 0,
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const [pdfBlob, setPdfBlob] = useState(null);
    const [orgOrder, setOrgOrder] = useState<any>(null);
    const [pendingInvoicesSummary, setPendingInvoicesSummary] = useState<{
        count: number;
        totalLeftToPay: number;
        oldestDaysOverdue: number | null;
    } | null>(null);
    const [showOrderMenu, setShowOrderMenu] = useState<boolean>(false);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [showTheoreticalStock, setShowTheoreticalStock] = useState<boolean>(false);
    const [theoreticalStockData, setTheoreticalStockData] = useState<any[]>([]);
    const [loadingTheoreticalStock, setLoadingTheoreticalStock] = useState<boolean>(false);
    const [accounts, setAccounts] = useState<any[]>([]);
    const [newSavNote, setNewSavNote] = useState<string>('');
    const [savSaving, setSavSaving] = useState<boolean>(false);

    const handleChange = (e: any) => {
        setOrder((prevCustomer: any) => ({
            ...prevCustomer,
            [e.target.id]: e.target.value,
        }));
    };

    const globalDiscount = (o: any) => ({
        globalDiscountType: o?.globalDiscountType,
        globalDiscountValue: o?.globalDiscountValue,
    });

    const getCustomerDeliveryTourName = () => {
        if (!order) return '';
        const customer = customers.find((c: any) => c._id === order.customerId);
        if (!customer) return 'Tournée par défaut';
        const deliveryTour = deliveryTours.find(
            (dt: any) => dt._id === customer.deliveryTourId,
        );
        return deliveryTour ? deliveryTour.name : 'Tournée par défaut';
    };

    const handleItemChange = (e: any) => {
        setNewItem((prevCustomer: any) => ({
            ...prevCustomer,
            [e.target.id]: e.target.value,
        }));
    };

    const updateQuantity = (product: any, value: number) => {
        setQuantityChanged(true);
        order.items.find((item: any) => item === product).quantity = value;
        order.totalPrice = getTotalHT(order.items, globalDiscount(order));
        setOrder({
            ...order,
        });
    };

    const updateLotNumber = (product: any, value: string | any[]) => {
        const target = order.items.find((item: any) => item === product);
        if (Array.isArray(value)) {
            target.preparationDetails = value;
            target.lotNumber = value[0]?.lotNumber || '';
        } else {
            target.preparationDetails = undefined;
            target.lotNumber = value;
        }
        setOrder({
            ...order,
        });
    };

    const updateComment = (product: any, value: number) => {
        order.items.find((item: any) => item === product).comment = value;
        setOrder({
            ...order,
        });
    };

    const updateUnityPrice = (product: any, value: number) => {
        order.items.find((item: any) => item === product).price = value;
        order.totalPrice = getTotalHT(order.items, globalDiscount(order));
        setOrder({
            ...order,
        });
    };

    const updateDiscount = (product: any, value: number) => {
        order.items.find((item: any) => item === product).discountValue = value;
        order.totalPrice = getTotalHT(order.items, globalDiscount(order));
        setOrder({
            ...order,
        });
    };

    const updateDiscountType = (product: any, value: string) => {
        order.items.find((item: any) => item === product).discountType = value;
        if (value === 'none') {
            order.items.find((item: any) => item === product).discountValue = 0;
        }
        order.totalPrice = getTotalHT(order.items, globalDiscount(order));
        setOrder({
            ...order,
        });
    };

    const updateGlobalDiscountType = (value: string) => {
        const updatedOrder = {
            ...order,
            globalDiscountType: value,
            globalDiscountValue: value === 'none' ? 0 : order.globalDiscountValue,
        };
        updatedOrder.totalPrice = getTotalHT(updatedOrder.items, globalDiscount(updatedOrder));
        setOrder(updatedOrder);
    };

    const updateGlobalDiscountValue = (value: number) => {
        const updatedOrder = { ...order, globalDiscountValue: value };
        updatedOrder.totalPrice = getTotalHT(updatedOrder.items, globalDiscount(updatedOrder));
        setOrder(updatedOrder);
    };

    const updatePackaging = (product: any, packaging: any, packagingIndex?: number) => {
        const updatedItems = order.items.map((item: any) => {
            if (item !== product) return item;
            if (packaging) {
                return {
                    ...item,
                    packagingRef: packaging.ref,
                    packagingUnity: packaging.unity,
                    packagingWeight: packaging.weight,
                    packagingBillByBaseUnit: packaging.billByBaseUnit || false,
                    packagingIndex: packagingIndex ?? null,
                    price: parseFloat(packaging.price),
                };
            } else {
                const { packagingRef, packagingUnity, packagingWeight, packagingBillByBaseUnit, packagingIndex: _pi, ...rest } = item;
                const fullProduct = products.find((p: any) => p._id === item.productId);
                return {
                    ...rest,
                    price: fullProduct ? parseFloat(fullProduct.price) : item.price,
                };
            }
        });
        const updatedOrder = {
            ...order,
            items: updatedItems,
            totalPrice: getTotalHT(updatedItems, globalDiscount(order)),
        };
        setOrder(updatedOrder);
    };

    const deleteItem = (item: any) => {
        setOrder((prevCustomer: any) => ({
            ...prevCustomer,
            items: prevCustomer.items.filter(
                (i: any) => i.productId !== item.productId,
            ),
            totalPrice: getTotalHT(
                prevCustomer.items.filter(
                    (i: any) => i.productId !== item.productId,
                ),
                globalDiscount(prevCustomer),
            ),
        }));
    };

    const reorderItems = (newItems: any[]) => {
        setOrder((prev: any) => ({ ...prev, items: newItems }));
    };

    const initOrder = async (id: string) => {
        const res = await getOrder(id);
        if (res) {
            setOrder(res);
            setOrgOrder(JSON.stringify(res));
            initProducts(res.customerId, res.items ?? []);
            fetchPendingInvoicesSummary(res.customerId);
            initCustomers(res.customerId);
            //setIsReady(res.isReady);
            return;
        }
        navigate('/orders');
    };

    const cleanOrder = (order: any) => {

        const newItems = order.items.map((item: any) => {
            const newItem = { ...item };
            newItem.price = toNumber(newItem.price);
            newItem.quantity = toNumber(newItem.quantity);
            if (newItem.discountValue !== undefined) newItem.discountValue = toNumber(newItem.discountValue);
            //delete newItem.totalPurchasePrice;
            return newItem;
        });
        return {
            ...order,
            items: newItems,
            globalDiscountValue: toNumber(order.globalDiscountValue),
        };
    };

    // The SAV follow-up thread is append-only via a dedicated endpoint — any
    // generic order save must never round-trip it, or the backend's closed Joi
    // schema rejects the whole request.
    const stripOrderForSave = (orderData: any) => {
        const data = { ...orderData };
        delete data.ownerId;
        delete data.createdAt;
        delete data.updatedAt;
        delete data.__v;
        delete data.viewed;
        delete data.idempotencyKey;
        if (data.sav) {
            const { history, ...savPayload } = data.sav;
            data.sav = savPayload;
        }
        return data;
    };

    const editOrder = async () => {
        const res = await updateOrder(cleanOrder(stripOrderForSave(order)));
        if (res.data) {
            toast.success('Modification enregistrée');
            navigate('/orders');
        } else if (res.offline) {
            toast.info(
                res.error?.message ||
                    'La mise à jour est en attente de synchronisation.',
            );
            navigate('/orders');
        } else {
            toast.error(
                res.error?.error ||
                    res.error?.message ||
                    (typeof res.error === 'string' ? res.error : null) ||
                    'Erreur lors de la sauvegarde de la commande',
            );
        }
    };
    const handleGenerateOrderNotePDF = async () => {
        try {
            const blob = await getOrderNote(params.id);
            //setPdfBlob(URL.createObjectURL(blob)); Use for testing
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `bon-commande-${order.orderId || ''}.pdf`;
            link.click();
        } catch (error) {
            console.error('Error generating PDF:', error);
        }
    };

    const handleGeneratePDF = async () => {
        try {
            const blob = await getOrderDeliveryNote(params.id);
            //setPdfBlob(URL.createObjectURL(blob)); Use for testing
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `bon-livraison-${order.orderId || ''}.pdf`;
            link.click();
        } catch (error) {
            console.error('Error generating PDF:', error);
        }
    };

    const handleGeneratePDFWithoutPrice = async () => {
        try {
            const blob = await getOrderDeliveryNoteWithoutPrice(params.id);
            //setPdfBlob(URL.createObjectURL(blob)); Use for testing
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = `bon-livraison-${order.orderId || ''}.pdf`;
            link.click();
        } catch (error) {
            console.error('Error generating PDF:', error);
        }
    };

    const sendDeliveryNoteByEmail = async () => {
        const res = await SendDeliveryNote(order._id);
        if (res) {
            toast.success('Bon de livraison envoyé par email');
            return;
        }
    };

    const getSelectedCustomer = () => {
        if (order && customers) {
            const customer = customers.find(
                (c: any) => c._id === order.customerId,
            );
            if (customer) {
                return customer;
            }
        }
        return null;
    };

    const isSelectedCustomerHT = () => isCustomerHT(getSelectedCustomer());

    const initCustomers = async (orderId?: string) => {
        const res = await getCustomers();
        const list: any[] = res?.data ?? [];
        setCustomers(list);
        // If the order's customer isn't in the initial 100, fetch them individually
        // so getSelectedCustomer() and the SearchSelector value display correctly.
        if (orderId && !list.find((c: any) => c._id === orderId)) {
            const customer = await getCustomer(orderId);
            if (customer) setCustomers((prev: any) => [customer, ...prev]);
        }
    };

    const initProducts = async (customerId: string, orderItems: any[] = []) => {
        // Only load the products actually referenced in this order — not the full catalog.
        // OrderTable uses products[] purely as a lookup dict by _id.
        const ids = [...new Set(orderItems.map((i: any) => i.productId).filter(Boolean))];
        if (ids.length === 0) {
            setIsFullyLoaded(true);
            return;
        }
        const res = await getProductsByIds(ids, customerId);
        if (res) {
            setProducts(res);
            setIsFullyLoaded(true);
        }
    };

    const initDeliveryTours = async () => {
        const res = await getDeliveryTours(false);
        if (res) {
            setDeliveryTours(res.data);
            return;
        }
    };

    const initAccounts = async () => {
        const res = await getUsersLight();
        if (res) setAccounts(res);
    };

    // Add totatPurchasePrice to each item
    useEffect(() => {
        if (!products || !order) return;
        const updatedOrder = { ...order };
        setOrder(updatedOrder);
    }, [products, isFullyLoaded]);

    useEffect(() => {
        if (localStorage.getItem('token') === null) {
            navigate('/login');
        }
        initOrder(params.id);
        // initCustomers is now called inside initOrder once the customerId is known
        initDeliveryTours();
        initAccounts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const updateProductsPrices = async (products: any) => {
        const newItemsUpdated = products.find(
            (product: any) => product._id === newItem._id,
        );
        let isNewOrderUpdated = false;
        const newOrdersItemsUpdated = order.items.map((item: any) => {
            const itemUpdated = products.find(
                (product: any) => product._id === item._id,
            );
            if (itemUpdated) {
                item.price = itemUpdated.price;
                isNewOrderUpdated = true;
            }
            return item;
        });

        const customer = customers.find(
            (customer: any) => customer._id === order.customerId,
        );
        const warning = `Prix unitaire HT modifié en fonction du prix négocié avec ${
            customer ? customer.company : 'le client'
        }.`;
        if (newItemsUpdated !== undefined) {
            setWarning(warning);
            setNewItem((prevCustomer: any) => ({
                ...prevCustomer,
                price: newItemsUpdated.price,
            }));
        }

        if (isNewOrderUpdated) {
            setWarning(warning);
            setOrder((prevCustomer: any) => ({
                ...prevCustomer,
                items: newOrdersItemsUpdated,
                totalPrice: getTotalHT(newOrdersItemsUpdated, globalDiscount(prevCustomer)),
            }));
        }
    };

    const fetchPendingInvoicesSummary = async (customerId: string) => {
        if (!customerId) {
            setPendingInvoicesSummary(null);
            return;
        }
        const res = await getNotPaidInvoicedPerCustomer(1000, 1, customerId);
        if (!res || res.error || !res.data?.data) {
            setPendingInvoicesSummary(null);
            return;
        }
        const invoices = res.data.data;
        const count = invoices.length;
        const totalLeftToPay = invoices.reduce((acc: number, inv: any) => {
            return acc + Math.max(0, Number(inv.leftToPay) || 0);
        }, 0);
        let oldestDate: Date | null = null;
        for (const inv of invoices) {
            const refDate = inv.dueDate || inv.invoicedAt || inv.createdAt;
            if (refDate) {
                const d = new Date(refDate);
                if (!oldestDate || d < oldestDate) oldestDate = d;
            }
        }
        const today = new Date();
        const oldestDaysOverdue =
            oldestDate !== null
                ? Math.floor(
                      (today.getTime() - oldestDate.getTime()) /
                          (1000 * 60 * 60 * 24),
                  )
                : null;
        setPendingInvoicesSummary({ count, totalLeftToPay, oldestDaysOverdue });
    };

    const fetchProductsPrices = async (customerId: string) => {
        setWarning('Chargement des prix personnalisés en cours...');
        // Reload only the products in the order with custom pricing for the new customer.
        const ids: string[] = [...new Set<string>((order?.items ?? []).map((i: any) => i.productId).filter(Boolean))];
        const res = ids.length > 0 ? await getProductsByIds(ids, customerId) : null;
        if (res) {
            setProducts(res);
            updateProductsPrices(res);
        }
    };

    const getStatusList = (order: any) => {
        let status = [
            {
                status: 'Brouillon',
                _id: 'draft',
            },
            {
                status: 'En cours',
                _id: 'pending',
            },
            {
                status: 'Livré',
                _id: 'delivered',
            },
            {
                status: 'Annulé',
                _id: 'canceled',
            },
            {
                status: 'Déjà facturé',
                _id: 'already-invoiced',
            },
        ];
        if (order && order.invoiced === true) {
            status = status.filter((s: any) => s._id !== 'canceled');
        }
        return status;
    };

    useEffect(() => {
        if (!defaultCustomer) {
            setDefaultCustomer(getSelectedCustomer());
            return;
        }
        if (order && order.customerId) {
            fetchProductsPrices(order.customerId);
            fetchPendingInvoicesSummary(order.customerId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order?.customerId]);

    const alreadyExist = (product: any) => {
        return order.items.find(
            (product: any) => product.productId === newItem.productId,
        );
    };

    const resolveIssue = async () => {
        const res = await resolveOrderIssue(order._id);
        if (res) {
            setOrder((prevCustomer: any) => ({
                ...prevCustomer,
                issueToResolve: false,
            }));
        }
    };

    const persistSav = async (updatedOrder: any) => {
        if (!updatedOrder?._id) return;
        const res = await updateOrder(cleanOrder(stripOrderForSave(updatedOrder)));
        if (!res?.data && !res?.offline) {
            toast.error('Erreur lors de la sauvegarde du SAV');
        }
    };

    // Due date / assignee / status / priority save immediately, same as the
    // history "Ajouter" button — waiting for the page's main "Enregistrer"
    // click would leave the linked agenda Task out of sync with what's shown.
    const setSavField = (field: string, value: any) => {
        const updated = { ...order, sav: { ...order?.sav, [field]: value } };
        setOrder(updated);
        persistSav(updated);
    };

    const addSavNote = async () => {
        if (!newSavNote.trim() || !order?._id) return;
        setSavSaving(true);
        const res = await addSavHistoryEntry(order._id, newSavNote.trim());
        setSavSaving(false);
        if (res) {
            setOrder((prev: any) => ({ ...prev, sav: res }));
            setNewSavNote('');
        } else {
            toast.error("Erreur lors de l'ajout de la note SAV");
        }
    };

    const resolveSav = async () => {
        if (!order?._id) return;
        const res = await resolveSavCase(order._id);
        if (res) {
            setOrder((prev: any) => ({ ...prev, sav: res }));
            toast.success('Dossier SAV marqué comme résolu');
        }
    };

    const isValidOrder = (order: any) => {
        return (
            orgOrder &&
            order.orderId &&
            order.status &&
            order.items.length > 0 &&
            order.deliveryDate &&
            order.customerId &&
            orgOrder !== JSON.stringify(order) &&
            order.totalPrice >= 0
        );
    };

    const totalBrutWeight = () => {
        return order.items
            .reduce(
                (acc: number, item: any) =>
                    acc +
                    (isNaN(Number(item.brutWeight * item.quantity))
                        ? 0
                        : Number(item.brutWeight * item.quantity)),
                0,
            )
            ?.toFixed(2);
    };

    const totalNetWeight = () => {
        return order.items
            .reduce(
                (acc: number, item: any) =>
                    acc +
                    (isNaN(Number(item.netWeight * item.quantity))
                        ? 0
                        : Number(item.netWeight * item.quantity)),
                0,
            )
            ?.toFixed(2);
    };

    const totalQuantity = () => {
        return order.items.reduce(
            (acc: number, item: any) =>
                acc + (isNaN(Number(item.quantity)) ? 0 : Number(item.quantity)),
            0,
        );
    };

    const generateInvoice = async () => {
        await editOrder();
        const res = await generateInvoiceForSpecificOrder(order._id);

        if (res.data) {
            toast.success('Facture générée avec succès');
            window.open(res.data.url, '_blank');
            setOrder((prevOrder: any) => ({
                ...prevOrder,
                invoiced: true,
            }));
        }
        onCloseGenerateInvoice();
    };

    const handleExportXLSX = () => {
        const customer = getSelectedCustomer();
        if (!customer || !order) return;

        // Préparer les données pour chaque produit de la commande
        const exportData = order.items.map((item: any) => {
            const product = products.find((p: any) => p._id === item.productId);
            return {
                'Reference': order.orderId || '',
                'Product - Sku': product?.ref || item.ref || '',
                'Product - Lot': item.lotNumber || '',
                'Product - Qty': item.quantity || '',
                'Product - Unit Price HT': '',
                'Product - Position Type': '',
                'Carrier code': 'VOID',
                'Relay Id': '',
                'Insurance': '',
                'Currency': 'EUR',
                'Comment': '',
                'Recipient - Company': customer.displayName || customer.company || '',
                'Recipient - Email': customer.mail || '',
                'Recipient - First name': customer.displayName || customer.company || '',
                'Recipient - Last name': customer.displayName || customer.company || '',
                'Recipient - Address 1': customer.deliveryAddress || '',
                'Recipient - Address 2': '',
                'Recipient - Address 3': '',
                'Recipient - Address 4': '',
                'Recipient - Phone Number': '0000000000',
                'Recipient - City': customer.deliveryCity || '',
                'Recipient - Zip Code': customer.deliveryZipCode || '',
                'Recipient - Country': 'FR',
                'Recipient - State': '',
                'Process Type': ''
            };
        });

        // Créer le fichier XLSX
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Commande');
        
        // Télécharger le fichier
        XLSX.writeFile(workbook, `commande-${order.orderId || order._id}.xlsx`);
        toast.success('Fichier XLSX téléchargé avec succès');
    };

    const handleExportOrderDetails = () => {
        if (!order) return;
        const exportData = order.items.map((item: any) => {
            const product = products.find((p: any) => p._id === item.productId);
            return {
                'Nom du produit': item.name || product?.name || '',
                'Code EAN': product?.ean || item.ean || '',
                'Référence': item.ref || product?.ref || '',
                'Quantité': item.quantity || 0,
                'Prix unitaire HT (€)': item.price || 0,
                'Prix de vente conseillé - PVC (€)': product?.recommendedPrice || '',
            };
        });
        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Commande');
        XLSX.writeFile(workbook, `export-commande-${order.orderId || order._id}.xlsx`);
        toast.success('Export téléchargé avec succès');
        setShowOrderMenu(false);
    };

    const fetchTheoreticalStock = async () => {
        if (!order || !order.items || order.items.length === 0) return;
        setLoadingTheoreticalStock(true);
        setShowTheoreticalStock(true);
        try {
            // '/orders?status=pending' matches both 'pending' AND 'draft' orders
            // server-side (see orderController.ts), so a single call already
            // covers both — fetching 'draft' separately would double-count them.
            const [stockRes, ordersRes] = await Promise.all([
                getStock(),
                getPaginatedOrders(1, 1000, 'pending', '', 'all', 'deliveryDate'),
            ]);
            const stockList: any[] = stockRes?.data || [];
            const allOrders: any[] = (ordersRes?.data || []).filter(
                (o: any) => String(o._id) !== String(order._id),
            );
            const committedMap = new Map<string, number>();
            for (const o of allOrders) {
                for (const item of o.items || []) {
                    const pid = String(item.productId?._id || item.productId || '');
                    if (!pid) continue;
                    const qty = Number(item.quantity) || 0;
                    committedMap.set(pid, (committedMap.get(pid) || 0) + qty);
                }
            }
            const result = order.items.map((item: any) => {
                const pid = String(item.productId?._id || item.productId || item._id || '');
                const stockItem = stockList.find(
                    (s: any) => String(s._id) === pid || String(s.productId) === pid,
                );
                const currentStock = Number(stockItem?.quantity ?? 0);
                const committed = committedMap.get(pid) || 0;
                const theoretical = currentStock - committed;
                return {
                    name: item.name,
                    ref: item.ref || stockItem?.ref || '',
                    currentStock,
                    committed,
                    theoretical,
                    unity: item.unity || stockItem?.unity || '',
                    orderedQty: Number(item.quantity) || 0,
                };
            });
            setTheoreticalStockData(result);
        } catch (e) {
            toast.error('Erreur lors du chargement du stock théorique');
        } finally {
            setLoadingTheoreticalStock(false);
        }
    };

    const handlePrintTrackingLabel = () => {
        const customer = getSelectedCustomer();
        const deliveryTour = deliveryTours.find(
            (dt: any) => dt._id === (order?.deliveryTourId || customer?.deliveryTourId)
        );

        // Data below comes from customer/order records (editable via CRM/CSV import) and is
        // interpolated into raw HTML fed to document.write, so it must be escaped to avoid XSS.
        const escapeHtml = (value: any): string =>
            String(value ?? '').replace(
                /[&<>"']/g,
                (c) =>
                    (
                        {
                            '&': '&amp;',
                            '<': '&lt;',
                            '>': '&gt;',
                            '"': '&quot;',
                            "'": '&#39;',
                        } as Record<string, string>
                    )[c],
            );

        const printWindow = window.open('', '_blank');
        if (!printWindow) return;

        const htmlContent = `
            <!DOCTYPE html>
            <html>
            <head>
                <meta charset="utf-8">
                <title>Étiquette de suivi - ${escapeHtml(order.orderId)}</title>
                <style>
                    @page {
                        size: 50mm 60mm;
                        margin: 2mm;
                    }
                    body {
                        font-family: Arial, sans-serif;
                        padding: 0;
                        margin: 0;
                        width: 50mm;
                        height: 60mm;
                    }
                    .label-container {
                        border: 1px solid #000;
                        padding: 3mm;
                        width: 100%;
                        height: 100%;
                        box-sizing: border-box;
                    }
                    .label-row {
                        margin-bottom: 2mm;
                        border-bottom: 1px solid #ccc;
                        padding-bottom: 1mm;
                    }
                    .label-row:last-child {
                        border-bottom: none;
                        margin-bottom: 0;
                    }
                    .label-title {
                        font-weight: bold;
                        font-size: 7px;
                        color: #666;
                        margin-bottom: 1mm;
                    }
                    .label-value {
                        font-size: 10px;
                        font-weight: bold;
                        color: #000;
                    }
                    .company-name {
                        font-size: 11px;
                        font-weight: bold;
                        color: #0260CB;
                    }
                    .address {
                        font-size: 8px;
                        line-height: 1.2;
                    }
                </style>
            </head>
            <body>
                <div class="label-container">
                    <div class="label-row">
                        <div class="label-title">N°BL</div>
                        <div class="label-value">${escapeHtml(order.orderId) || 'N/A'}</div>
                    </div>
                    <div class="label-row">
                        <div class="label-title">N° de la tournée</div>
                        <div class="label-value">${escapeHtml(deliveryTour?.name) || 'Tournée par défaut'}</div>
                    </div>
                    <div class="label-row">
                        <div class="label-title">Client</div>
                        <div class="company-name">${escapeHtml(customer?.company) || 'N/A'}</div>
                    </div>
                    <div class="label-row">
                        <div class="label-title">Adresse de livraison</div>
                        <div class="address">
                            ${escapeHtml(customer?.deliveryAddress) || 'Adresse non renseignée'}<br/>
                            ${escapeHtml([customer?.deliveryZipCode, customer?.deliveryCity].filter(Boolean).join(' '))}
                        </div>
                    </div>
                </div>
                <script>
                    window.onload = function() {
                        window.print();
                    };
                </script>
            </body>
            </html>
        `;

        printWindow.document.write(htmlContent);
        printWindow.document.close();
    };

    return (
        <div className="px-2 sm:px-4 lg:px-0">
            <GoBack />
            <div className="mt-1 grid grid-cols-1 lg:grid-cols-12 gap-2 sm:gap-3">
                <DeleteOrder
                    isOpen={isOpen}
                    onClose={onClose}
                    description="Êtes-vous sûr de vouloir supprimer cette commande ?"
                    context="d'une commande"
                    archived={false}
                    deleteFunction={() => {
                        deleteOrder(params.id);
                        navigate('/orders');
                    }}
                />
                <GenerateInvoice
                    isOpen={isOpenGenerateInvoice}
                    onClose={onCloseGenerateInvoice}
                    onClick={generateInvoice}
                />
                {/* Modal stock  */}
                <Modal isOpen={showTheoreticalStock} onClose={() => setShowTheoreticalStock(false)}>
                    <ModalOverlay className="bg-[#000] !opacity-30" />
                    <ModalContent className="!z-[1002] !m-auto !w-max min-w-[350px] !max-w-[90%] top-[8vh]">
                        <ModalBody>
                            <Card extra="px-[30px] pt-[35px] pb-[40px] max-w-[850px] flex flex-col !z-[1004] max-h-[80vh] overflow-y-auto">
                                <div className="flex items-start justify-between mb-5">
                                    <div>
                                        <h1 className="text-xl font-bold text-navy-700">Stock théorique</h1>
                                        <p className="text-xs text-gray-400 mt-1">
                                            Stock actuel − engagements (commandes + brouillons, hors cette commande)
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => setShowTheoreticalStock(false)}
                                        className="ml-6 text-gray-400 hover:text-gray-600 text-xl leading-none"
                                    >
                                        ✕
                                    </button>
                                </div>
                                {loadingTheoreticalStock ? (
                                    <p className="text-sm text-gray-500 py-6 text-center">Chargement en cours...</p>
                                ) : (
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-sm text-left border-collapse">
                                            <thead>
                                                <tr className="border-b border-gray-100">
                                                    <th className="pb-2 pr-6 text-xs font-semibold text-gray-400 uppercase">Produit</th>
                                                    <th className="pb-2 px-4 text-xs font-semibold text-gray-400 uppercase text-right">Commandé</th>
                                                    <th className="pb-2 px-4 text-xs font-semibold text-gray-400 uppercase text-right">Stock actuel</th>
                                                    <th className="pb-2 px-4 text-xs font-semibold text-gray-400 uppercase text-right">Engagé</th>
                                                    <th className="pb-2 pl-4 text-xs font-semibold text-gray-400 uppercase text-right">Stock théorique (sans cette commande)</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {theoreticalStockData.map((row: any, idx: number) => (
                                                    <tr key={idx} className="border-b border-gray-50 hover:bg-gray-50">
                                                        <td className="py-2.5 pr-6">
                                                            <p className="font-medium text-navy-700">{row.name}</p>
                                                            {row.ref && <p className="text-xs text-gray-400">{row.ref}</p>}
                                                        </td>
                                                        <td className="py-2.5 px-4 text-right text-sm font-medium text-[#0260CB]">
                                                            {Number(row.orderedQty).toFixed(2).replace(/\.?0+$/, '')} {row.unity}
                                                        </td>
                                                        <td className="py-2.5 px-4 text-right text-sm text-gray-600">
                                                            {Number(row.currentStock).toFixed(2).replace(/\.?0+$/, '')} {row.unity}
                                                        </td>
                                                        <td className="py-2.5 px-4 text-right text-sm text-orange-500">
                                                            {row.committed > 0 ? `− ${Number(row.committed).toFixed(2).replace(/.?0+$/, '')} ${row.unity}` : <span className="text-gray-300">—</span>}
                                                        </td>
                                                        <td className="py-2.5 pl-4 text-right">
                                                            <span className={`text-sm font-bold ${
                                                                row.theoretical < 0
                                                                    ? 'text-red-500'
                                                                    : row.theoretical < row.orderedQty
                                                                    ? 'text-orange-500'
                                                                    : 'text-green-600'
                                                            }`}>
                                                                {Number(row.theoretical).toFixed(2).replace(/\.?0+$/, '')} {row.unity}
                                                            </span>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                <div className="mt-5 flex justify-end">
                                    <button
                                        onClick={() => setShowTheoreticalStock(false)}
                                        className="linear rounded-xl border-2 border-gray-300 px-5 py-2 text-sm font-medium text-gray-700 transition duration-200 hover:bg-gray-50"
                                    >
                                        Fermer
                                    </button>
                                </div>
                            </Card>
                        </ModalBody>
                    </ModalContent>
                </Modal>
                <AddProductsModal
                    isOpen={isOpenAddProduct}
                    onClose={onCloseAddProduct}
                    addProducts={(newItems: any) => {
                        setOrder((prev: any) => ({
                            ...prev,
                            items: [...prev.items, ...newItems],
                            totalPrice: getTotalHT([...prev.items, ...newItems], globalDiscount(prev)),
                        }));
                        onCloseAddProduct();
                    }}
                    existingProducts={order ? order.items.map((item: any) => item._id) : []}
                    customerId={order?.customerId}
                />
                <Card extra={'col-span-1 lg:col-span-8 w-full !p-3 sm:!p-5 border'}>
                    {/* Header */}
                    <div className="h-full w-full">
                        <div className="relative">
                            <div className="flex items-center gap-3">
                                <h4 className="text-base sm:text-lg font-medium text-navy-700 dark:text-white">
                                    Commande n°{order ? order.orderId : ''}
                                </h4>
                                <button
                                    className="linear cursor-pointer items-center justify-center rounded-lg border border-green-600 px-2 py-1 text-xs text-green-600 transition duration-200 hover:bg-green-50 active:bg-green-100 whitespace-nowrap"
                                    onClick={handleExportXLSX}
                                >
                                    Export XLSX
                                </button>
                                {/* Three-dot menu */}
                                <div className="relative ml-auto">
                                    <button
                                        className="flex items-center justify-center w-7 h-7 rounded-full hover:bg-gray-100 text-gray-500 hover:text-gray-700 transition-colors"
                                        onClick={() => setShowOrderMenu((v) => !v)}
                                        title="Plus d'options"
                                    >
                                        <span className="text-lg font-bold leading-none tracking-widest">⋯</span>
                                    </button>
                                    {showOrderMenu && (
                                        <>
                                            <div
                                                className="fixed inset-0 z-40"
                                                onClick={() => setShowOrderMenu(false)}
                                            />
                                            <div className="absolute right-0 top-8 z-50 w-52 rounded-xl border border-gray-200 bg-white shadow-lg py-1">
                                                <button
                                                    className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                                                    onClick={handleExportOrderDetails}
                                                >
                                                     Export commande
                                                </button>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                            <div className="flex flex-wrap gap-1 sm:gap-2 mt-3">
                                <button
                                    className="hidden sm:flex linear cursor-pointer items-center justify-center rounded-xl border border-[#01499b] px-2 py-1 text-xs text-[#01499b] transition duration-200 hover:bg-brand-600/5 active:bg-red-100 dark:active:bg-red-50 dark:active:opacity-80"
                                    onClick={handleGenerateOrderNotePDF}
                                >
                                    Générer un bon de commande
                                </button>
                                <button
                                    className="hidden sm:flex linear cursor-pointer items-center justify-center rounded-xl border border-[#01499b] px-2 py-1 text-xs text-[#01499b] transition duration-200 hover:bg-brand-600/5 active:bg-red-100 dark:active:bg-red-50 dark:active:opacity-80"
                                    onClick={handleGeneratePDFWithoutPrice}
                                >
                                    Générer BL non valorisé
                                </button>
                                <button
                                    className="hidden sm:flex linear cursor-pointer items-center justify-center rounded-xl border border-[#01499b] px-2 py-1 text-xs text-[#01499b] transition duration-200 hover:bg-brand-600/5 active:bg-red-100 dark:active:bg-red-50 dark:active:opacity-80"
                                    onClick={handleGeneratePDF}
                                >
                                    Générer un bon de livraison
                                </button>
                                <button
                                    className="hidden sm:flex linear cursor-pointer items-center gap-2  justify-center rounded-xl border border-[#01499b] px-2 py-1 text-xs text-[#01499b] transition duration-200 hover:bg-brand-600/5 active:bg-red-100 dark:active:bg-red-50 dark:active:opacity-80"
                                    onClick={sendDeliveryNoteByEmail}
                                >
                                    <MdEmail size={13} />
                                    Envoyer le BL
                                </button>
                                {order && !order.isReady && !order.invoiced && order.status !== 'already-invoiced' && (
                                    <GeneratePreparationButton
                                        orderId={order._id}
                                        orderStatus={order.status}
                                        isReady={order.isReady}
                                    />
                                )}
                                {order && !order.invoiced && order.status !== 'already-invoiced' && (
                                    <button
                                        className="hidden sm:flex linear cursor-pointer items-center justify-center rounded-xl border border-[#01499b] px-2 py-1 text-xs text-[#01499b] transition duration-200 hover:bg-brand-600/5 active:bg-red-100 dark:active:bg-red-50 dark:active:opacity-80"
                                        onClick={onOpenGenerateInvoice}
                                    >
                                        Facturer la commande
                                    </button>
                                )}
                                <button
                                    className="flex sm:hidden w-[50px] h-[50px] linear cursor-pointer items-center justify-center rounded-full px-2 py-2 text-xs text-[#01499b] transition duration-200 hover:bg-brand-600/5 active:bg-red-100 dark:active:bg-red-50 dark:active:opacity-80"
                                    onClick={handleGeneratePDF}
                                >
                                    <LuFileSpreadsheet size={30} />
                                </button>
                            </div>
                            <div className="h-full w-full">
                                {/* <p className="mt-1 text-base text-gray-600">
                                    {order && order.invoiced
                                        ? 'Commande déjà facturée, disponible en lecture-seule.'
                                        : ''}
                                </p> */}
                                {pdfBlob && (
                                    <div className="">
                                        <Document
                                            className="flex w-full border rounded-3xl bg-gray-400"
                                            file={pdfBlob}
                                        >
                                            <Page pageNumber={1} />
                                        </Document>
                                    </div>
                                )}
                                <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3">
                                    <div className="col-span-1 sm:col-span-1 lg:col-span-3">
                                        <InputField
                                            label="Date de livraison*"
                                            placeholder="01/01/2021"
                                            id="deliveryDate"
                                            type="date"
                                            value={
                                                order && order.deliveryDate
                                                    ? order.deliveryDate.split(
                                                          'T',
                                                      )[0]
                                                    : ''
                                            }
                                            onChange={handleChange}
                                            disabled={
                                                order &&
                                                order.invoiced === false
                                                    ? false
                                                    : true
                                            }
                                            inputSmall={true}
                                        />
                                    </div>
                                    <div className="col-span-1 sm:col-span-1 lg:col-span-3">
                                        <SearchSelector
                                            label="Statut"
                                            options={getStatusList(order)}
                                            displayKey="status"
                                            value={order ? order.status : ''}
                                            onSelect={(e: any) => {
                                                setOrder(
                                                    (prevCustomer: any) => ({
                                                        ...prevCustomer,
                                                        status: e._id,
                                                    }),
                                                );
                                            }}
                                            inputSmall={true}
                                        />
                                        {order &&
                                            order.status === 'canceled' && (
                                                <p className="mt-1  ml-3 text-xs text-gray-400">
                                                    💡 Pour garantir une
                                                    traçabilitée parfaite pensez
                                                    à retirer le contenu de la
                                                    commande de votre stock
                                                    après annulation.
                                                </p>
                                            )}
                                        {order &&
                                            order.status === 'already-invoiced' && (
                                                <p className="mt-1  ml-3 text-xs text-gray-400">
                                                    💡 Cette commande ne sera pas facturable 
                                                    car elle est marquée comme déjà facturée.
                                                </p>
                                            )}
                                    </div>
                                    <div className="col-span-1 sm:col-span-1 lg:col-span-3">
                                        <SearchSelector
                                            label="Client*"
                                            displayKey="company"
                                            options={customers}
                                            onSearch={searchCustomersForSelect}
                                            value={
                                                order ? order.customerId : ''
                                            }
                                            disabled={
                                                order &&
                                                order.invoiced === false
                                                    ? false
                                                    : true
                                            }
                                            onSelect={(e: any) => {
                                                setOrder(
                                                    (prevCustomer: any) => ({
                                                        ...prevCustomer,
                                                        customerId: e._id,
                                                    }),
                                                );
                                            }}
                                            inputSmall={true}
                                        />
                                    </div>
                                    <div className="col-span-1 sm:col-span-1 lg:col-span-3">
                                        <InputField
                                            label="Numéro purchase order (PO)"
                                            placeholder="PO-7578"
                                            id="purchaseOrderNumber"
                                            type="text"
                                            value={
                                                order
                                                    ? order.purchaseOrderNumber
                                                    : ''
                                            }
                                            onChange={handleChange}
                                            inputSmall={true}
                                        />
                                    </div>
                                </div>
                                <div className="mt-2 grid grid-cols-1 lg:grid-cols-12 gap-3">
                                    <div className="col-span-1 lg:col-span-6">
                                        <InputField
                                            label="Note du client"
                                            placeholder={`Pouvez-vous livrer avant 8h ?`}
                                            id="customerNotes"
                                            type="text"
                                            value={
                                                order ? order.customerNotes : ''
                                            }
                                            disabled={
                                                order &&
                                                order.invoiced === false
                                                    ? false
                                                    : true
                                            }
                                            onChange={handleChange}
                                            inputSmall={true}
                                        />
                                    </div>
                                    {order?.origin === 'command-page' && order?.contactEmail && (
                                        <div className="col-span-1 lg:col-span-3">
                                            <label className="text-xs font-medium text-navy-700 dark:text-white">
                                                Commandé par
                                            </label>
                                            <p className="text-sm text-gray-700 mt-1">{order.contactEmail}</p>
                                        </div>
                                    )}
                                    <div className="col-span-1 lg:col-span-3">
                                        {defaultDeliveryTour &&
                                        !order?.deliveryTourId ? (
                                            <>
                                                <label
                                                    htmlFor={
                                                        'deliverytourlabel'
                                                    }
                                                    className={`text-xs font-medium text-navy-700 dark:text-white`}
                                                >
                                                    Tournée de livraison
                                                </label>
                                                <p className="text-sm">
                                                    {getCustomerDeliveryTourName()}
                                                </p>
                                                <p
                                                    className="text-xs text-toola-500 cursor-pointer hover:underline"
                                                    onClick={() =>
                                                        setDefaultDeliveryTour(
                                                            false,
                                                        )
                                                    }
                                                >
                                                    Modifier sur cette commande
                                                </p>
                                            </>
                                        ) : (
                                            <SearchSelector
                                                label="Tournée de livraison"
                                                displayKey="name"
                                                options={deliveryTours}
                                                value={order?.deliveryTourId}
                                                onSelect={(e: any) => {
                                                    setOrder((prev: any) => ({
                                                        ...prev,
                                                        deliveryTourId: e._id,
                                                    }));
                                                }}
                                                canCreate={true}
                                                createNew={() =>
                                                    window.open(
                                                        '/settings/deliveries',
                                                        '_blank',
                                                    )
                                                }
                                                inputSmall={true}
                                            />
                                        )}
                                    </div>
                                    <div className="col-span-1 lg:col-span-3">
                                        <InputField
                                            label={`Note interne`}
                                            placeholder="Livraison prioritaire"
                                            id="internalNotes"
                                            type="text"
                                            value={
                                                order ? order.internalNotes : ''
                                            }
                                            disabled={
                                                order &&
                                                order.invoiced === false
                                                    ? false
                                                    : true
                                            }
                                            onChange={handleChange}
                                            inputSmall={true}
                                        />
                                    </div>
                                    <div className="col-span-1 lg:col-span-12">
                                        <InputField
                                            label={`Note de livraison (figure sur le BL)`}
                                            placeholder="Livraison prioritaire"
                                            id="deliveryNotes"
                                            type="text"
                                            value={
                                                order ? order.deliveryNotes : ''
                                            }
                                            disabled={
                                                order &&
                                                order.invoiced === false
                                                    ? false
                                                    : true
                                            }
                                            onChange={handleChange}
                                            inputSmall={true}
                                        />
                                    </div>
                                </div>
                                {order && !isReady && (
                                    <div className="flex flex-col sm:flex-row gap-2 mt-4">
                                        <button
                                            className="border border-toola-400 text-toola-400 text-xs px-3 py-1.5 rounded-lg hover:bg-toola-50 whitespace-nowrap flex-shrink-0"
                                            onClick={async (e: any) => {
                                                e.stopPropagation();
                                                await markOrderReady(
                                                    order?._id,
                                                );
                                                setIsReady(true);
                                            }}
                                        >
                                            Terminer la préparation
                                        </button>
                                        <button
                                            className="border border-blue-600 text-blue-600 text-xs px-3 py-1.5 rounded-lg hover:bg-blue-50 whitespace-nowrap flex-shrink-0"
                                            onClick={(e: any) => {
                                                e.stopPropagation();
                                                handlePrintTrackingLabel();
                                            }}
                                        >
                                            Imprimer étiquette de suivi
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {order && order.issueToResolve && (
                        <div className="mt-3 p-4 rounded-xl shadow-lg border">
                            <p className="text-red-500 text-sm">
                                Un problème ou un commentaire de livraison est
                                ouvert pour cette commande (voir note de
                                livraison).
                            </p>
                            <p
                                className="mt-2 text-sm p-2 text-center cursor-pointer rounded-xl bg-red-300 border"
                                onClick={resolveIssue}
                            >
                                Marquer comme résolu
                            </p>
                        </div>
                    )}
                </Card>
                <Card extra={'w-full !p-3 sm:!p-5 border col-span-1 lg:col-span-4'}>
                    {/* Header */}
                    <div className="h-full w-full">
                        <div className="relative">
                            <div className="flex gap-[10px] absolute right-0 top-0"></div>
                            <div className="h-full w-full">
                                <div className="flex justify-between items-start">
                                    <h4 className="text-base sm:text-lg font-medium text-navy-700 dark:text-white">
                                        Client
                                    </h4>
                                    <div className="">
                                        <button
                                            className="linear flex cursor-pointer items-center justify-center rounded-xl border border-red-500 px-2 py-1 sm:py-2 text-[10px] sm:text-xs text-red-500 transition duration-200 hover:bg-red-50 active:bg-red-100 dark:active:bg-red-50 dark:active:opacity-80 whitespace-nowrap"
                                            onClick={() => onOpen()}
                                        >
                                            Supprimer la commande
                                        </button>
                                    </div>
                                </div>
                                <p className="mt-2 text-xs sm:text-sm text-gray-600 break-words">
                                    {getSelectedCustomer() &&
                                        `Nom: ${
                                            getSelectedCustomer().company
                                        }`}{' '}
                                </p>
                                <p className="mt-2 text-xs sm:text-sm text-gray-600 break-words">
                                    {getSelectedCustomer() &&
                                        `Email: ${
                                            getSelectedCustomer().mail
                                        }`}{' '}
                                </p>
                                <p className="mt-2 text-xs sm:text-sm text-gray-600 break-words">
                                    {getSelectedCustomer() &&
                                        `Téléphone: ${
                                            getSelectedCustomer().phone ||
                                            'Non renseigné'
                                        }`}
                                    {!getSelectedCustomer() &&
                                        'Aucune information client'}
                                </p>
                                {getSelectedCustomer()?.francoPort && (
                                    <p className="mt-2 text-xs sm:text-sm text-gray-600 break-words">
                                        {`Franco de port: ${
                                            getSelectedCustomer().francoPort
                                        }`}
                                    </p>
                                )}
                                {pendingInvoicesSummary &&
                                    pendingInvoicesSummary.count > 0 && (
                                        <div className="mt-3 p-2 sm:p-3 bg-orange-50 border border-orange-200 rounded-lg">
                                            <p className="text-xs font-semibold text-orange-700">
                                                ⚠️{' '}
                                                {pendingInvoicesSummary.count}{' '}
                                                facture
                                                {pendingInvoicesSummary.count > 1
                                                    ? 's'
                                                    : ''}{' '}
                                                en attente
                                            </p>
                                            <p className="text-xs text-gray-600 mt-1">
                                                À régler :{' '}
                                                <span className="font-bold text-orange-600">
                                                    {pendingInvoicesSummary.totalLeftToPay.toFixed(2)}€
                                                </span>
                                            </p>
                                            {pendingInvoicesSummary.oldestDaysOverdue !== null && (
                                                <p className="text-xs text-gray-600 mt-0.5">
                                                    Retard (+ ancienne) :{' '}
                                                    <span
                                                        className={`font-bold ${
                                                            pendingInvoicesSummary.oldestDaysOverdue > 30
                                                                ? 'text-red-600'
                                                                : 'text-orange-600'
                                                        }`}
                                                    >
                                                        {pendingInvoicesSummary.oldestDaysOverdue}{' '}
                                                        jour
                                                        {pendingInvoicesSummary.oldestDaysOverdue > 1
                                                            ? 's'
                                                            : ''}
                                                    </span>
                                                </p>
                                            )}
                                        </div>
                                    )}
                                <p
                                    className="flex gap-2 mt-2 text-xs sm:text-sm text-blue-500 hover:cursor-pointer break-words"
                                    onClick={() =>
                                        window.open(
                                            `/customer/${order.customerId}`,
                                            '_blank',
                                        )
                                    }
                                >
                                    <FaExternalLinkAlt
                                        size={12}
                                        className="flex my-auto"
                                    />
                                    Voir la fiche client
                                </p>
                            </div>
                        </div>
                    </div>
                </Card>
                {order && (
                    <Card extra={'col-span-1 lg:col-span-12 w-full !p-3 sm:!p-5 border'}>
                        <div className="flex items-center justify-between">
                            <h4 className="text-base sm:text-lg font-medium text-navy-700 dark:text-white">
                                Suivi SAV
                            </h4>
                            {order.sav?.status && (
                                <span
                                    className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                                        order.sav.status === 'resolved'
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-orange-100 text-orange-700'
                                    }`}
                                >
                                    {SAV_STATUS_OPTIONS.find((s) => s._id === order.sav.status)?.label}
                                </span>
                            )}
                        </div>
                        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-3">
                            <div className="col-span-1 sm:col-span-1 lg:col-span-3">
                                <InputField
                                    label="Échéance SAV"
                                    id="savDueDate"
                                    type="date"
                                    value={
                                        order.sav?.dueDate ? order.sav.dueDate.split('T')[0] : ''
                                    }
                                    onChange={(e: any) => setSavField('dueDate', e.target.value)}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-1 sm:col-span-1 lg:col-span-3">
                                <SearchSelector
                                    label="Assigné à"
                                    options={accounts}
                                    displayKey="fullName"
                                    value={order.sav?.assignedTo || ''}
                                    onSelect={(e: any) => setSavField('assignedTo', e._id)}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-1 sm:col-span-1 lg:col-span-3">
                                <SearchSelector
                                    label="Statut"
                                    options={SAV_STATUS_OPTIONS}
                                    displayKey="label"
                                    value={order.sav?.status || 'open'}
                                    onSelect={(e: any) => setSavField('status', e._id)}
                                    inputSmall={true}
                                />
                            </div>
                            <div className="col-span-1 sm:col-span-1 lg:col-span-3">
                                <SearchSelector
                                    label="Priorité"
                                    options={SAV_PRIORITY_OPTIONS}
                                    displayKey="label"
                                    value={order.sav?.priority || 'medium'}
                                    onSelect={(e: any) => setSavField('priority', e._id)}
                                    inputSmall={true}
                                />
                            </div>
                        </div>

                        <div className="mt-4">
                            <p className="text-sm font-medium text-navy-700 dark:text-white mb-2">
                                Suivi
                            </p>
                            <div className="max-h-[260px] overflow-y-auto flex flex-col gap-2 pr-1">
                                {!order.sav?.history || order.sav.history.length === 0 ? (
                                    <p className="text-xs text-gray-400">
                                        Aucune note pour le moment.
                                    </p>
                                ) : (
                                    order.sav.history.map((entry: any, i: number) => (
                                        <div key={i} className="bg-lightPrimary rounded-lg p-2.5">
                                            <p className="text-xs text-gray-500">
                                                <span className="font-semibold text-navy-700">
                                                    {entry.authorName}
                                                </span>
                                                {' — '}
                                                {new Date(entry.createdAt).toLocaleString('fr-FR')}
                                            </p>
                                            <p className="text-sm text-navy-700 mt-0.5 whitespace-pre-wrap">
                                                {entry.text}
                                            </p>
                                        </div>
                                    ))
                                )}
                            </div>
                            <div className="mt-3 flex flex-col sm:flex-row gap-2">
                                <textarea
                                    className="flex-1 rounded-xl border border-gray-200 p-2.5 text-sm text-navy-700 focus:outline-none focus:ring-2 focus:ring-toola-400"
                                    rows={2}
                                    placeholder="Ajouter une note de suivi SAV..."
                                    value={newSavNote}
                                    onChange={(e) => setNewSavNote(e.target.value)}
                                    disabled={!order._id}
                                />
                                <button
                                    className="flex-shrink-0 rounded-xl bg-toola-400 text-white text-sm px-4 py-2 disabled:opacity-50"
                                    disabled={!newSavNote.trim() || savSaving || !order._id}
                                    onClick={addSavNote}
                                >
                                    Ajouter
                                </button>
                            </div>
                            {!order._id && (
                                <p className="mt-1 text-xs text-gray-400">
                                    Enregistrez la commande pour pouvoir ajouter une note de suivi.
                                </p>
                            )}
                        </div>

                        {order.sav && order.sav.status !== 'resolved' && (
                            <div className="mt-4">
                                <button
                                    className="text-sm px-3 py-1.5 rounded-lg border border-green-400 text-green-600 hover:bg-green-50"
                                    onClick={resolveSav}
                                >
                                    Marquer le SAV comme résolu
                                </button>
                            </div>
                        )}
                    </Card>
                )}
                <Card extra={'col-span-1 lg:col-span-12 w-full !p-3 sm:!p-5 border'}>
                    {/* Header */}
                    <div className="h-full w-full">
                        <div>
                            <div className="flex items-center justify-between">
                                <h4 className="text-base sm:text-lg font-medium text-navy-700 dark:text-white">
                                    Contenu de la commande
                                </h4>
                                <button
                                    className="linear flex items-center gap-1 justify-center rounded-lg border border-toola-500 px-2 py-1 text-xs text-toola-500 transition duration-200 hover:bg-toola-50 whitespace-nowrap"
                                    onClick={fetchTheoreticalStock}
                                >
                                    Stock théorique
                                </button>
                            </div>
                            {order && !order.invoiced && (
                                <>
                                    {warning && (
                                        <p className="mt-1 text-xs sm:text-sm text-red-500">
                                            {warning}
                                        </p>
                                    )}
                                    <div className="mt-3 flex items-center gap-3">
                                        <button
                                            className="linear flex items-center gap-1 justify-center rounded-xl bg-toola-500 hover:bg-toola-600 active:bg-toola-700 px-3 py-1.5 text-xs font-medium text-white whitespace-nowrap"
                                            onClick={onOpenAddProduct}
                                        >
                                            Parcourir les produits
                                        </button>
                                    </div>
                                </>
                            )}
                            <div className="mt-4 flex items-center gap-3">
                                <p className="text-sm sm:text-base text-gray-600 whitespace-nowrap">
                                    Récapitulatif de la commande
                                </p>
                                <div className="relative flex-1 max-w-xs">
                                    <input
                                        type="text"
                                        placeholder="Rechercher un produit…"
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full h-8 pl-8 pr-3 text-xs border border-gray-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-toola-400 focus:border-toola-400"
                                    />
                                    <svg className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                                        <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                                    </svg>
                                    {searchQuery && (
                                        <button
                                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                            onClick={() => setSearchQuery('')}
                                        >✕</button>
                                    )}
                                </div>
                            </div>
                            {order && (
                                <OrderTable
                                    order={order}
                                    products={products}
                                    handleQtyChange={updateQuantity}
                                    handleLotNumberChange={updateLotNumber}
                                    handlePriceChange={updateUnityPrice}
                                    handleDiscountChange={updateDiscount}
                                    handleDiscountTypeChange={
                                        updateDiscountType
                                    }
                                    handleCommentChange={updateComment}
                                    handlePackagingChange={updatePackaging}
                                    onDelete={deleteItem}
                                    onReorder={reorderItems}
                                    customerId={order?.customerId}
                                    searchQuery={searchQuery}
                                />
                            )}
                        </div>
                        {quantityChanged && (
                            <p className="mt-4 text-sm text-red-500">
                                <p className="mt-1  ml-3 text-sm text-gray-400">
                                    💡 Pour garantir une traçabilitée parfaite
                                    pensez à réajuster votre stock après édition
                                    de la commande.
                                </p>
                            </p>
                        )}
                        {order && order.items && order.items.length > 4 && (
                            <div className="mt-2 flex gap-2">
                                <p
                                    className="flex my-auto text-toola-500 hover:cursor-pointer hover:text-toola-600"
                                    onClick={() => {
                                        window.scrollTo({
                                            top: 0,
                                            behavior: 'auto',
                                        }); // behaviors: smooth, auto, instant
                                    }}
                                >
                                    Ajouter un produit
                                </p>
                                <FaArrowUpLong
                                    size={10}
                                    className="flex my-auto text-toola-500"
                                />
                            </div>
                        )}
                        {order && order.items && order.items.length > 0 && (
                            <div className="mt-3 sm:mt-5 flex items-center gap-2">
                                <p className="text-sm sm:text-base text-navy-700 dark:text-white whitespace-nowrap">
                                    Réduction globale:
                                </p>
                                <div className="w-[100px]">
                                    <SearchSelector
                                        displayKey="name"
                                        value={order.globalDiscountType || 'none'}
                                        options={[
                                            { name: 'Aucune', _id: 'none' },
                                            { name: '%', _id: 'percent' },
                                            { name: '€', _id: 'value' },
                                        ]}
                                        onSelect={(e: any) => updateGlobalDiscountType(e._id)}
                                        inputSmall={true}
                                    />
                                </div>
                                {order.globalDiscountType && order.globalDiscountType !== 'none' && (
                                    <div className="flex items-center gap-1">
                                        <InputField
                                            extra="w-[70px]"
                                            placeholder="0"
                                            id="globalDiscountValue"
                                            type="number"
                                            value={order.globalDiscountValue}
                                            onChange={(e: any) => updateGlobalDiscountValue(e.target.value)}
                                            inputSmall={true}
                                        />
                                        <span className="text-xs text-gray-600">
                                            {order.globalDiscountType === 'percent' ? '%' : '€'}
                                        </span>
                                    </div>
                                )}
                            </div>
                        )}
                        {order && order.items && order.items.length > 0 && (
                            <div className="flex flex-wrap gap-3 sm:gap-6 lg:gap-10">
                                <div className="mt-3 sm:mt-5 flex items-center gap-2 min-w-0">
                                    <p className="text-sm sm:text-base lg:text-lg text-navy-700 dark:text-white whitespace-nowrap">
                                        Total HT:
                                    </p>
                                    <p className="text-base sm:text-lg font-bold text-navy-700 dark:text-white">
                                    {getTotalHT(order.items, globalDiscount(order)).toFixed(2)}€
                                    </p>
                                </div>
                                {getGlobalDiscountAmount(order.items, order.globalDiscountType, order.globalDiscountValue) > 0 && (
                                    <div className="mt-3 sm:mt-5 flex items-center gap-2 min-w-0">
                                        <p className="text-sm sm:text-base lg:text-lg text-navy-700 dark:text-white whitespace-nowrap">
                                            Réduction globale:
                                        </p>
                                        <p className="text-base sm:text-lg font-bold text-navy-700 dark:text-white">
                                        -{getGlobalDiscountAmount(order.items, order.globalDiscountType, order.globalDiscountValue).toFixed(2)}€
                                        </p>
                                    </div>
                                )}
                                {getTotalTTC(order.items, {
                                    isCustomerHT: isSelectedCustomerHT(),
                                    ...globalDiscount(order),
                                }) > 0 && (
                                    <div className="mt-3 sm:mt-5 flex items-center gap-2 min-w-0">
                                        <p className="text-sm sm:text-base lg:text-lg text-navy-700 dark:text-white whitespace-nowrap">
                                            Total TTC:
                                        </p>
                                        <p className="text-base sm:text-lg font-bold text-navy-700 dark:text-white">
                                        {getTotalTTC(order.items, {
                                            isCustomerHT: isSelectedCustomerHT(),
                                            ...globalDiscount(order),
                                        }).toFixed(2)}€
                                        </p>
                                    </div>
                                )}
                                {totalNetWeight() > 0 && (
                                    <div className="mt-3 sm:mt-5 flex items-center gap-2 min-w-0">
                                        <p className="text-sm sm:text-base lg:text-lg text-navy-700 dark:text-white whitespace-nowrap">
                                            Poids net:
                                        </p>
                                        <p className="text-base sm:text-lg font-bold text-navy-700 dark:text-white">
                                            {totalNetWeight()}
                                            Kg
                                        </p>
                                    </div>
                                )}
                                {totalBrutWeight() > 0 && (
                                    <div className="mt-3 sm:mt-5 flex items-center gap-2 min-w-0">
                                        <p className="text-sm sm:text-base lg:text-lg text-navy-700 dark:text-white whitespace-nowrap">
                                            Poids brut:
                                        </p>
                                        <p className="text-base sm:text-lg font-bold text-navy-700 dark:text-white">
                                            {totalBrutWeight()}
                                            Kg
                                        </p>
                                    </div>
                                )}
                                <div className="mt-3 sm:mt-5 flex items-center gap-2 min-w-0">
                                    <p className="text-sm sm:text-base lg:text-lg text-navy-700 dark:text-white whitespace-nowrap">
                                        Qté totale:
                                    </p>
                                    <p className="text-base sm:text-lg font-bold text-navy-700 dark:text-white">
                                        {totalQuantity()}
                                    </p>
                                </div>
                                {order.numberOfArticles > 0 && (
                                    <div className="mt-3 sm:mt-5 flex items-center gap-2 min-w-0">
                                        <p className="text-sm sm:text-base lg:text-lg text-navy-700 dark:text-white whitespace-nowrap">
                                            Nb Art.:
                                        </p>
                                        <p className="text-base sm:text-lg font-bold text-navy-700 dark:text-white">
                                            {order.numberOfArticles}
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div className="col-span-1">
                            <InputField
                                label="Nombre de palettes"
                                placeholder="0"
                                id="pcbTotal"
                                type="number"
                                value={order?.pcbTotal ?? ''}
                                disabled={order && order.invoiced === false ? false : true}
                                onChange={handleChange}
                                inputSmall={true}
                            />
                        </div>
                        <div className="col-span-1">
                            <InputField
                                label="Préparé par"
                                placeholder="Lucas"
                                id="preparedBy"
                                type="string"
                                value={order?.preparedBy ?? ''}
                                disabled={order && order.invoiced === false ? false : true}
                                onChange={handleChange}
                                inputSmall={true}
                            />
                        </div>
                    </div>
                </Card>
                {order && (
                    <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-5 px-2 sm:px-0">
                        <button
                            className={`linear flex items-center justify-center rounded-lg px-5 py-2.5 text-sm font-medium min-w-[140px] ${
                                isValidOrder(order)
                                    ? 'bg-toola-500 hover:bg-toola-600 active:bg-toola-700 text-white'
                                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                            }`}
                            onClick={() => {
                                isValidOrder(order) && editOrder();
                            }}
                        >
                            Sauvegarder
                        </button>
                        <button
                            className="linear border border-toola-500 text-toola-500 hover:bg-toola-50 active:bg-toola-100 flex items-center justify-center rounded-lg px-5 py-2.5 text-sm font-medium whitespace-nowrap min-w-[140px]"
                            onClick={() => {
                                navigate(`/create-order`);
                            }}
                        >
                            + nouvelle commande
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EditOrder;
